import { useState, lazy, Suspense } from 'react';

import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import RootLayout from './components/layouts/RootLayout';
import PublicLayout from './components/layouts/PublicLayout';
import PrivateRoutes from './components/auth/PrivateRoute';
import AdminPrivateRoute from './components/auth/AdminPrivateRoute';
import RootRedirect from './components/auth/RootRedirect';
import AccountSettingsLayout from './components/layouts/AccountSettingsLayout';
import GradientLogo from '../public/icons/GradientLogo';
import PostSkeleton from './components/pages/Skeletons/PostSkeleton';
import './Routes.css';
import PageLoadingCircle from './components/assetComponents/PageLoadingCircle';
import NavLayout from './components/layouts/NavLayout';

// Lazy loaded components
const HomePage = lazy(() => import('./components/pages/HomePage/HomePage'));
const HomePagePublic = lazy(() =>
  import('./components/pages/HomePage/HomePagePublic')
);
const NewItemPage = lazy(() =>
  import('./components/pages/PostNewItemPage/NewItemPage')
);
const NewItemPreviewPage = lazy(() =>
  import('./components/pages/PostNewItemPage/NewItemPreviewPage')
);
const MyItemsAndPosts = lazy(() =>
  import('./components/pages/MyItemsAndPostsPage/MyItemsAndPosts')
);

const ViewMyItemsAndPosts = lazy(() =>
  import('./components/pages/MyItemsAndPostsPage/ViewMyItemsAndPosts')
);

const SettingsPage = lazy(() =>
  import('./components/pages/Settings/SettingsPage')
);

// Fixed: Use consistent import for ViewPost
const ViewPost = lazy(() =>
  import('./components/pages/HomePage/posts/ViewPost/ViewPostWrapper')
);

const LogInPage = lazy(() => import('./components/pages/LogIn/LogInPage'));
const SignUpPage = lazy(() => import('./components/pages/SignUp/SignUpPage'));

import { SignUpPage2 } from './components/pages/SignUp/SignUpPage';

const EmailVerification = lazy(() =>
  import('./components/pages/EmailVerification/EmailVerification')
);
const EmailVerificationSuccess = lazy(() =>
  import('./components/pages/EmailVerification/EmailVerificationSuccess')
);
const PasswordReset = lazy(() =>
  import('./components/pages/ForgotPassword/PasswordReset')
);
const PasswordResetCode = lazy(() =>
  import('./components/pages/ForgotPassword/PasswordResetCode')
);
const PasswordResetSuccess = lazy(() =>
  import('./components/pages/ForgotPassword/PasswordResetSuccess')
);
const NotFound = lazy(() => import('./components/pages/NotFound'));
const PrivacyPolicy = lazy(() =>
  import('./components/pages/privacypolicy/PrivacyPolicy')
);
const TsAndCs = lazy(() => import('./components/pages/TsAndCs/TsAndCs'));
const TipsAndRecomendations = lazy(() =>
  import('./components/pages/TipsAndRecomendations/TipsAndRecomendations')
);

const Dashboard = lazy(() => import('./components/pages/dashboard/Dashboard'));

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path='/' element={<RootLayout />}>
      {/* Root redirect - sends users to appropriate page based on auth status */}
      <Route index element={<RootRedirect />} />

      {/* Private Routes */}
      <Route
        path='/swapandtop/'
        element={
          <Suspense
            fallback={
              <div className='splash-logo-wrapper'>
                <GradientLogo svgClass={'splash-logo'} />
              </div>
            }>
            <PrivateRoutes />
          </Suspense>
        }>
        <Route
          index
          element={
            <Suspense
              fallback={
                <div className='post-skeleton-wrapper'>
                  <PostSkeleton />
                  <PostSkeleton />
                  <PostSkeleton />
                </div>
              }>
              <HomePage />
            </Suspense>
          }
        />
        <Route
          path='postnewitem'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <NewItemPage />
            </Suspense>
          }
        />
        <Route
          path='postnewitempreview'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <NewItemPreviewPage />
            </Suspense>
          }
        />
        <Route
          path='myitemsandposts'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <MyItemsAndPosts />
            </Suspense>
          }
        />
        <Route
          path='myposts'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <ViewMyItemsAndPosts />
            </Suspense>
          }
        />
        <Route
          path='watchlist'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <ViewMyItemsAndPosts />
            </Suspense>
          }
        />
        <Route
          path='settings'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <SettingsPage />
            </Suspense>
          }>
          <Route path='account' element={<AccountSettingsLayout />} />
        </Route>

        {/* Fixed: ViewPost route for private access */}
        <Route
          path='viewpost/:id'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <ViewPost />
            </Suspense>
          }
        />
      </Route>

      {/* Public Routes */}
      <Route
        path='/swapandtop/admin'
        element={
          <AdminPrivateRoute>
            <NavLayout />
          </AdminPrivateRoute>
        }>
        <Route
          index
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard />
            </Suspense>
          }
        />
        <Route
          path='dashboard'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard />
            </Suspense>
          }
        />
        <Route
          path='posts'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard type='posts' />
            </Suspense>
          }
        />
        <Route
          path='users'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard type='users' />
            </Suspense>
          }
        />
        <Route
          path='reports'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard type='reports' />
            </Suspense>
          }
        />
        <Route
          path='analytics'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard type='analytics' />
            </Suspense>
          }
        />
        <Route
          path='settings'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard type='settings' />
            </Suspense>
          }
        />
        <Route
          path='security'
          element={
            <Suspense
              fallback={
                <div className='loading-fallback'>
                  <PageLoadingCircle />
                </div>
              }>
              <Dashboard type='security' />
            </Suspense>
          }
        />
      </Route>

      <Route
        path='/swapandtop/posts'
        element={
          <Suspense
            fallback={
              <div className='splash-logo-wrapper'>
                <GradientLogo svgClass={'splash-logo'} />
              </div>
            }>
            <PublicLayout />
          </Suspense>
        }>
        <Route
          index
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <HomePagePublic />
            </Suspense>
          }
        />

        {/* Fixed: Corrected path structure for public viewpost */}
        <Route
          path='viewpost/:id'
          element={
            <Suspense
              fallback={
                <PageLoadingCircle
                  pageHeight={
                    'page-loading-circle-element-wrapper-normal-height'
                  }
                />
              }>
              <ViewPost />
            </Suspense>
          }
        />
      </Route>

      {/* Authentication Routes */}
      <Route
        path='/swapandtop/login'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <LogInPage />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/signup'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <SignUpPage />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/signup2'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <SignUpPage2 />
          </Suspense>
        }
      />

      {/* Other Routes */}
      <Route
        path='/swapandtop/emailverification'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <EmailVerification />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/emailverificationsuccess'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <EmailVerificationSuccess />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/tou'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <TsAndCs />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/privacypolicy'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <PrivacyPolicy />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/tipsandrecomandations'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <TipsAndRecomendations />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/forgotpassword'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <PasswordReset />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/resetpassword'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <PasswordResetCode />
          </Suspense>
        }
      />
      <Route
        path='/swapandtop/resetpasswordsuccess'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <PasswordResetSuccess />
          </Suspense>
        }
      />

      {/* Catch-all route */}
      <Route
        path='*'
        element={
          <Suspense
            fallback={
              <PageLoadingCircle
                pageHeight={'page-loading-circle-element-wrapper-full-height'}
              />
            }>
            <NotFound />
          </Suspense>
        }
      />
    </Route>
  )
);

export default router;
